import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, Copy, Check, Loader2, Clock, CheckCircle2, XCircle, ExternalLink, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { DragonTransferFlow, actionForInvoice, openExternalAction } from "@/lib/dragontransfer.mjs";
import QRCode from "react-qr-code";
import { getCryptoIconUrl, getCryptoIconFallback } from "@/lib/cryptoIcons";
import { openSupportChat } from "@/lib/support";
import { RubQuotes } from "@/lib/rubQuotes.mjs";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";

const allCryptos = [
  { currency: "USDT", name: "Tether" },
  { currency: "USDC", name: "USD Coin" },
  { currency: "TON", name: "Toncoin" },
  { currency: "SOL", name: "Solana" },
  { currency: "TRX", name: "TRON" },
  { currency: "BTC", name: "Bitcoin" },
  { currency: "ETH", name: "Ethereum" },
];

const TERMINAL = ["credited", "expired", "canceled", "rejected", "failed"];
const CYRILLIC_RE = /^[А-Яа-яЁё]+(?:[ -][А-Яа-яЁё]+)*$/;
const PHONE_RE = /^\+?\d{10,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fmtCrypto = (v, cur) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  const dec = ["USDT", "USDC", "BTC", "ETH"].includes(cur) || n < 1 ? 6 : 2;
  return n.toFixed(dec).replace(/\.?0+$/, "");
};
const fmtRub = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2).replace(/\.?0+$/, "");
};
const fmtInt = (v) => {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("ru-RU");
};
const fmtClock = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

const openPayment = (url) => {
  if (!url) return;
  try {
    if (window?.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(url);
      return;
    }
  } catch {
    /* fall through */
  }
  window.open(url, "_blank", "noopener");
};

const InlinePageHeader = ({ title, subtitle, onBack, showBack = true }) => (
  <div className="lp-topbar px-4 py-3">
    {showBack && (
      <button type="button" onClick={onBack} className="inline-flex h-8 w-8 items-center justify-center text-foreground" aria-label="Back">
        <ChevronLeft className="w-5 h-5" />
      </button>
        )}
    {title && <div className="min-w-0"><h1 className="font-semibold">{title}</h1>{subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}</div>}
  </div>
);

const Field = ({ label, value, onChange, placeholder, error, testid, type = "text" }) => (
  <div>
    <label htmlFor={testid} className="text-sm text-muted-foreground mb-1 block">{label}</label>
    <input
      id={testid}
      name={testid}
      maxLength={type === "email" ? 254 : type === "tel" ? 30 : 100}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-foreground outline-none border",
        error ? "border-red-400" : "border-transparent"
      )}
      data-testid={testid}
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const RubTopUpPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user, telegram, refreshData, haptic, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
  const showInlineBack = !isTelegramWebApp;
  const flow = useMemo(() => new DragonTransferFlow(api, { telegram: isTelegramWebApp }), [api, user?.id, isTelegramWebApp]);
  const quotes = useMemo(() => new RubQuotes(api), [api, user?.id]);
  const [restoring, setRestoring] = useState(true);
  const [restoreError, setRestoreError] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [instantQuote, setInstantQuote] = useState(false);
  const [quoteError, setQuoteError] = useState(false);
  const [quoteRevision, setQuoteRevision] = useState(0);
  const [uncertainAttempt, setUncertainAttempt] = useState(false);
  const creditedOnce = useRef(new Set());
  const refreshRef = useRef(refreshData);
  refreshRef.current = refreshData;

  const [config, setConfig] = useState(null);
  const [currency, setCurrency] = useState("USDT");
  const [mode, setMode] = useState("rub");
  const [amount, setAmount] = useState("");
  const [quoteState, setQuote] = useState(null);
  const quoteKey = JSON.stringify([currency, mode, amount]);
  const quote = quoteState?._inputKey === quoteKey ? quoteState : null;
  const [quoting, setQuoting] = useState(false);
  const [step, setStep] = useState("amount"); // 'amount' | 'customer'
  const [pii, setPii] = useState({ first_name: "", second_name: "", middle_name: "", phone: "", email: "" });
  const [creating, setCreating] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [anchorRate, setAnchorRate] = useState(null); // курс ₽/ед. для пересчёта лимитов в крипту

  const cryptoOptions = useMemo(() => {
    const sup = config?.supported_currencies;
    if (Array.isArray(sup) && sup.length) {
      return allCryptos.filter((c) => sup.includes(c.currency));
    }
    return allCryptos;
  }, [config]);

  const isRedirect = config?.payment_kind === "redirect";
  const requiresCustomer = !!config?.requires_customer;
  const singleCurrency = cryptoOptions.length === 1;
  const soleCurrency = cryptoOptions[0]?.currency || currency;

  const restoreInvoice = useCallback(async () => {
    setRestoring(true);
    setRestoreError(false);
    try {
      const [cfg, saved] = await Promise.all([
        flow.getConfig().catch(() => ({ enabled: false })),
        flow.restore(new URLSearchParams(location.search).get("order_id")),
      ]);
      setConfig(cfg);
      setInvoice(saved);
      if (saved?.target_currency) setCurrency(saved.target_currency);
      else if (cfg?.supported_currencies?.length) setCurrency(cfg.supported_currencies[0]);
      setUncertainAttempt(Boolean(flow.attempt && !saved));
    } catch {
      setRestoreError(true);
    } finally {
      setRestoring(false);
    }
  }, [flow, location.search]);

  useEffect(() => { void restoreInvoice(); }, [restoreInvoice]);

  useEffect(() => {
    setPii({ first_name: "", second_name: "", middle_name: "", phone: "", email: "" });
    setInvoice(null);
    setUncertainAttempt(false);
  }, [flow]);

  const handleBack = useCallback(() => {
    haptic?.("light");
    if (invoice || creating || uncertainAttempt) return navigate("/topup");
    if (step === "customer") return setStep("amount");
    navigate("/topup");
  }, [haptic, invoice, step, navigate, creating, uncertainAttempt]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  // Cached presets resolve immediately; only typed input needs a debounce.
  useEffect(() => {
    const val = Number(amount);
    setQuote(null);
    setQuoteError(false);
    if (!config?.enabled || restoring || invoice || !Number.isFinite(val) || val <= 0 || !/^\d+(?:\.\d{1,6})?$/.test(amount) || (mode === "rub" && ((amount.split(".")[1]?.length || 0) > 2 || val < config.min_rub || val > config.max_rub))) {
      setQuoting(false);
      setQuote(null);
      return;
    }
    let cancelled = false;
    const body = { target_currency: currency, [mode === "rub" ? "rub_amount" : "crypto_amount"]: val };
    const cached = quotes.peek(body);
    if (cached) {
      setQuote({ ...cached, _inputKey: quoteKey });
      setQuoting(false);
      return;
    }
    setQuoting(true);
    const id = setTimeout(async () => {
      try {
        const data = await quotes.get(body);
        if (!cancelled) setQuote({ ...data, _inputKey: quoteKey });
      } catch {
        if (!cancelled) { setQuote(null); setQuoteError(true); }
      } finally {
        if (!cancelled) setQuoting(false);
      }
    }, instantQuote ? 0 : 250);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [quotes, currency, mode, amount, config?.enabled, config?.min_rub, config?.max_rub, restoring, invoice, quoteRevision, instantQuote, quoteKey]);

  // Якорный курс: одна котировка на минимум, чтобы знать границы в крипте до ввода.
  useEffect(() => {
    const min = Number(config?.min_rub);
    if (!config?.enabled || restoring || invoice || !Number.isFinite(min) || min <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await quotes.get({ target_currency: currency, rub_amount: min });
        const r = Number(data?.rate_rub_per_unit);
        if (!cancelled && Number.isFinite(r) && r > 0) setAnchorRate(r);
      } catch {
        /* оставим null — до ввода границы в крипте просто не покажем */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [quotes, currency, config?.enabled, config?.min_rub, restoring, invoice]);

  const piiErrors = useMemo(() => {
    const e = {};
    if (pii.first_name && !CYRILLIC_RE.test(pii.first_name.trim())) e.first_name = t("rubtopup.name_cyrillic_error");
    if (pii.second_name && !CYRILLIC_RE.test(pii.second_name.trim())) e.second_name = t("rubtopup.name_cyrillic_error");
    if (pii.middle_name && !CYRILLIC_RE.test(pii.middle_name.trim())) e.middle_name = t("rubtopup.name_cyrillic_error");
    if (pii.phone && !PHONE_RE.test(pii.phone.replace(/[\s()-]/g, ""))) e.phone = t("rubtopup.phone_error");
    if (pii.email && !EMAIL_RE.test(pii.email.trim())) e.email = t("rubtopup.email_error");
    return e;
  }, [pii, t]);

  const piiComplete =
    pii.first_name && pii.second_name && pii.phone && pii.email && Object.keys(piiErrors).length === 0;

  const createInvoice = async () => {
    const val = Number(amount);
    if (!Number.isFinite(val) || val <= 0 || creating || restoring || restoreError || !config?.enabled) return;
    haptic?.("medium");
    setCreating(true);
    try {
      const body = { target_currency: currency };
      if (mode === "rub") body.rub_amount = val;
      else body.crypto_amount = val;
      if (requiresCustomer) {
        body.user_email = pii.email.trim();
        body.customer = {
          first_name: pii.first_name.trim(),
          second_name: pii.second_name.trim(),
          middle_name: pii.middle_name.trim(),
          phone: pii.phone.trim(),
        };
      }
      const data = config?.provider === "dragontransfer"
        ? await flow.create(flow.attempt?.draft || body)
        : (await api.post("/rub-topup/invoice", body)).data;
      setInvoice(data);
      flow.invoice = data;
      setUncertainAttempt(false);
      const action = actionForInvoice(data);
      if (action && !action.expired) openExternalAction(action);
    } catch (err) {
      // Разбираем ТОЛЬКО коды, для которых у нас есть свой текст. Остальные
      // detail'ы написаны для интегратора и на английском («Payment rail not
      // configured», «Unsupported currency»): показывать их человеку — значит
      // рассказывать ему про наши рельсы вместо того, что ему делать.
      setUncertainAttempt(Boolean(flow.attempt));
      const detail = err?.response?.data?.detail;
      let msg = t("rubtopup.create_error");
      if (typeof detail === "string") {
        if (detail.startsWith("amount_below_min"))
          msg = t("rubtopup.below_min", { amount: fmtInt(detail.split(":")[1]) });
        else if (detail.startsWith("amount_above_max"))
          msg = t("rubtopup.above_max", { amount: fmtInt(detail.split(":")[1]) });
      }
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const onPrimary = () => {
    if (requiresCustomer && step === "amount") {
      if (!quote) return;
      setStep("customer");
      return;
    }
    createInvoice();
  };

  // Sequential polling preserves the saved invoice on transport errors.
  useEffect(() => {
    if (!invoice?.order_id || TERMINAL.includes(invoice.status)) return;
    let stopped = false;
    let timer;
    const tick = async () => {
      try {
        const saved = await flow.refresh();
        if (!stopped) { setInvoice(saved); setStatusError(false); }
      } catch {
        if (!stopped) setStatusError(true);
      } finally {
        if (!stopped) timer = setTimeout(tick, 5000);
      }
    };
    void tick();
    return () => { stopped = true; clearTimeout(timer); };
  }, [flow, invoice?.order_id, invoice?.status]);

  const checkStatus = async () => {
    if (refreshing || !invoice?.order_id) return;
    setRefreshing(true);
    try { setInvoice(await flow.refresh()); setStatusError(false); }
    catch { setStatusError(true); }
    finally { setRefreshing(false); }
  };

  useEffect(() => {
    const resume = () => {
      if (document.visibilityState === "visible" && flow.invoice?.order_id) {
        void flow.refresh().then(saved => { setInvoice(saved); setStatusError(false); }).catch(() => setStatusError(true));
      }
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    return () => { document.removeEventListener("visibilitychange", resume); window.removeEventListener("focus", resume); };
  }, [flow]);

  useEffect(() => {
    if (invoice?.status !== "credited" || creditedOnce.current.has(invoice.order_id)) return;
    creditedOnce.current.add(invoice.order_id);
    void refreshRef.current?.();
    haptic?.("success");
  }, [invoice, haptic]);

  useEffect(() => {
    if (!invoice) return;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [invoice]);

  const secondsLeft = useMemo(() => {
    const action = actionForInvoice(invoice, now);
    const raw = action?.kind === "verification" ? invoice?.verification_expires_at
      : action?.kind === "payment" ? invoice?.invoice_expires_at : invoice?.expires_at;
    const exp = Number(raw);
    return raw && Number.isFinite(exp) ? Math.max(0, exp - now) : null;
  }, [invoice, now]);

  const minRub = Number(config?.min_rub) || 0;
  const maxRub = Number(config?.max_rub) || 0;
  const rate = Number(quote?.rate_rub_per_unit) || Number(anchorRate) || 0;
  const cryptoPresetCur = currency === "USDT" || currency === "USDC";
  // Границы в валюте ввода. Крипто-границы округляем ВНУТРЬ окна: мин вверх, макс вниз,
  // чтобы пресет/ввод по этим числам не выпал за рублёвый лимит провайдера.
  const minUnit = mode === "rub" ? minRub : rate ? Math.ceil((minRub / rate) * 100) / 100 : 0;
  const maxUnit = mode === "rub" ? maxRub : rate ? Math.floor((maxRub / rate) * 100) / 100 : 0;
  const unitLabel = mode === "rub" ? "₽" : currency;
  const minDisp = mode === "rub" ? fmtInt(minRub) : fmtCrypto(minUnit, currency);
  const maxDisp = mode === "rub" ? fmtInt(maxRub) : fmtCrypto(maxUnit, currency);

  // Валидация — всегда по рублёвому эквиваленту (у провайдера лимит в ₽, это источник истины).
  const rubValue = mode === "rub" ? Number(amount) : quote ? Number(quote.rub_amount) : null;
  const rangeError = useMemo(() => {
    if (rubValue == null || !Number.isFinite(rubValue) || rubValue <= 0) return null;
    if (minRub && rubValue < minRub) return { key: "below_min", amount: minDisp, unit: unitLabel };
    if (maxRub && rubValue > maxRub) return { key: "above_max", amount: maxDisp, unit: unitLabel };
    return null;
  }, [rubValue, minRub, maxRub, minDisp, maxDisp, unitLabel]);

  const presets = useMemo(() => {
    if (mode === "rub") {
      if (!minRub && !maxRub) return [];
      return [...new Set([minRub, 5000, 10000, 25000, 50000, 100000])]
        .filter((v) => (!minRub || v >= minRub) && (!maxRub || v <= maxRub))
        .map((v) => ({ value: v, label: `${fmtInt(v)} ₽` }));
    }
    if (!cryptoPresetCur || !minUnit || !maxUnit) return [];
    return [100, 250, 500, 1000, 2500, 5000]
      .filter((v) => v >= minUnit && v <= maxUnit)
      .map((v) => ({ value: v, label: `${fmtInt(v)} ${currency}` }));
  }, [mode, minRub, maxRub, minUnit, maxUnit, cryptoPresetCur, currency]);

  useEffect(() => {
    if (config?.provider !== "dragontransfer" || !config?.enabled || restoring || invoice || mode !== "rub") return;
    let cancelled = false;
    const bodies = presets.map(p => ({ target_currency: currency, rub_amount: p.value }));
    void quotes.prefetch(bodies, () => cancelled);
    return () => { cancelled = true; };
  }, [quotes, presets, currency, mode, config?.provider, config?.enabled, restoring, invoice]);

  const handleCopy = async (text) => {
    if (!text) return;
    haptic?.("light");
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      toast.success(t("rubtopup.copied"));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t("rubtopup.copy_error"));
    }
  };

  const handleCancel = async () => {
    if (!invoice?.order_id || closing) return;
    haptic?.("light");
    setClosing(true);
    try {
      flow.invoice = invoice;
      if (invoice.provider === "dragontransfer") {
        await flow.close();
        setConfirmClose(false);
        setInvoice(null);
        setUncertainAttempt(false);
        setStep("amount");
        setAmount("");
        navigate("/rub-topup", { replace: true });
      } else {
        await flow.dismiss();
        navigate("/topup");
      }
    } catch (err) {
      toast.error(t(err?.response?.data?.detail === "creation_in_progress" ? "rubtopup.creating" : "rubtopup.cancel_error"));
    } finally { setClosing(false); }
  };

  // ------------------------------------------------------------------ Инвойс
  if (invoice) {
    const status = invoice.status;
    const isCredited = status === "credited";
    const isClosed = Boolean(invoice.user_closed_at);
    const isDead = isClosed || ["expired", "canceled", "rejected", "failed"].includes(status);
    const dragon = invoice.provider === "dragontransfer";
    const action = dragon ? actionForInvoice(invoice, now) : null;
    const review = ["payment_review", "deposit_review", "aml_review"].includes(status);
    const uncertain = ["creating", "creation_unknown"].includes(status);
    const missingAction = dragon && ["pending", "kyc_pending", "payin_pending"].includes(status) && !action;
    const needsHelp = uncertain || review || missingAction || action?.expired;
    const stage = ["pending", "kyc_pending"].includes(status) ? 0 : status === "payin_pending" ? 1 : isCredited ? 3 : 2;
    const redirect = dragon || !!invoice.payment_url;
    return (
      <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col" data-testid="rubtopup-invoice-page">
        <InlinePageHeader title={t("rubtopup.title")} subtitle={singleCurrency ? soleCurrency : undefined} onBack={handleBack} showBack={showInlineBack} />
        <div className="flex-1 overflow-y-auto overscroll-contain pb-[calc(5rem+var(--lp-bottom))]" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="p-6 space-y-5">
            {dragon && !isDead && !uncertain && <ol className="flex gap-2 text-xs text-muted-foreground" aria-label={t("rubtopup.progress")}>
              {["step_kyc", "step_payment", "step_transfer", "step_complete"].map((key, index) => <li key={key} className={cn("flex-1 border-t-2 pt-2", index <= stage ? "border-primary text-foreground" : "border-border")} aria-current={index === stage ? "step" : undefined}>{t(`rubtopup.${key}`)}</li>)}
            </ol>}
            {isCredited && (
              <div className="flex flex-col items-center gap-2 py-6" data-testid="rubtopup-credited">
                <CheckCircle2 className="w-14 h-14 text-green-500" />
                <p className="text-lg font-semibold">{t("rubtopup.credited_title")}</p>
                <p className="text-sm text-muted-foreground">
                  +{fmtCrypto(invoice.credited_amount || invoice.receive_amount, currency)} {currency}
                </p>
                <button onClick={() => navigate("/topup")} className="mt-4 w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold">
                  {t("rubtopup.done")}
                </button>
              </div>
            )}

            {isDead && !isCredited && (
              <div className="flex flex-col items-center gap-2 py-6" data-testid="rubtopup-dead">
                <XCircle className="w-14 h-14 text-gray-400" />
                <p className="text-lg font-semibold">{t(isClosed ? "rubtopup.status_closed" : `rubtopup.status_${status}`)}</p>
                {invoice.error_hint && (
                  <p className="text-sm text-muted-foreground text-center max-w-xs" data-testid="rubtopup-error-hint">
                    {invoice.error_hint}
                  </p>
                )}
                {dragon && <p className="text-sm text-muted-foreground text-center">{t(isClosed ? "rubtopup.closed_hint" : "rubtopup.failure_hint")}</p>}
                {isClosed && <button onClick={checkStatus} disabled={refreshing} className="w-full py-3 rounded-xl border" data-testid="rubtopup-refresh">{t("rubtopup.check_status")}</button>}
                <button onClick={() => { flow.invoice = null; flow.attempt = null; setInvoice(null); setStep("amount"); setUncertainAttempt(false); navigate("/rub-topup", { replace: true }); }} className="mt-4 w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold">
                  {t("rubtopup.new_invoice")}
                </button>
              </div>
            )}

            {statusError && <p role="alert" className="text-sm text-amber-600">{t("rubtopup.restore_error")}</p>}
            {dragon && <button onClick={() => handleCopy(invoice.order_id)} className="w-full text-xs text-muted-foreground break-all">{t("rubtopup.copy_reference")}: {invoice.order_id}</button>}
            {!isCredited && !isDead && (
              <>
                <div className="flex items-center justify-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <Clock className="w-4 h-4" />
                  {dragon ? t(`rubtopup.status_${status}`) : secondsLeft != null
                    ? t(dragon ? "rubtopup.step_expires_in" : "rubtopup.expires_in", { time: fmtClock(secondsLeft) })
                    : t("rubtopup.waiting")}
                </div>

                {redirect ? (
                  <div className="space-y-4">
                    <p className="text-center text-sm text-muted-foreground" role={uncertain ? "alert" : undefined}>{dragon ? t(uncertain ? "rubtopup.unknown_hint" : missingAction ? "rubtopup.preparing_link_hint" : "rubtopup.amount_due", { amount: fmtRub(invoice.rub_amount_exact || invoice.rub_amount) }) : t("rubtopup.pay_on_page")}</p>
                    {(!dragon || action) && <button
                      onClick={() => dragon ? openExternalAction(action) : openPayment(invoice.payment_url)}
                      disabled={Boolean(action?.expired)}
                      className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid={action?.kind === "verification" ? "rubtopup-open-verification" : "rubtopup-open-payment"}
                    >
                      <ExternalLink className="w-5 h-5" />
                      {t(action?.kind === "verification" ? "rubtopup.open_verification" : "rubtopup.open_payment")}
                    </button>}
                    {action && secondsLeft != null && !action.expired && <p className="text-xs text-muted-foreground text-center">{t("rubtopup.step_expires_in", { time: fmtClock(secondsLeft) })}</p>}
                    {!uncertain && <p className="text-xs text-muted-foreground text-center">{t(action?.expired ? "rubtopup.link_expired_hint" : review ? "rubtopup.review_hint" : action?.kind === "verification" ? "rubtopup.after_kyc_hint" : "rubtopup.processing_hint")}</p>}
                    {dragon && <button onClick={checkStatus} disabled={refreshing} className="w-full py-3 rounded-xl border disabled:opacity-50" data-testid="rubtopup-refresh">{refreshing ? t("rubtopup.checking") : t("rubtopup.check_status")}</button>}
                    {needsHelp && <button onClick={() => openSupportChat(telegram)} className="w-full py-3 rounded-xl border" data-testid="rubtopup-support">{t("profile.help_support")}</button>}
                  </div>
                ) : (
                  <>
                    <div className="flex justify-center">
                      <div className="bg-white p-4 rounded-2xl shadow-sm">
                        <QRCode value={invoice.qr_payload || invoice.order_id} size={200} level="M" data-testid="rubtopup-qr" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground">{t("rubtopup.pay_exactly")}</label>
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 flex items-center gap-3">
                        <p className="text-xl font-semibold flex-1 text-foreground" data-testid="rubtopup-amount">
                          {fmtRub(invoice.rub_amount_exact)} ₽
                        </p>
                        <button onClick={() => handleCopy(invoice.rub_amount_exact)} className={cn("p-2 rounded-lg transition-colors", copied ? "bg-green-100 text-green-600" : "bg-white hover:bg-gray-100 text-gray-900")} data-testid="rubtopup-copy">
                          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <p className="text-xs text-muted-foreground text-center">
                  {t(uncertain ? "rubtopup.expected_receive" : "rubtopup.will_receive", { amount: fmtCrypto(invoice.receive_amount, currency), currency })}
                </p>

                {Array.isArray(invoice.requisites) && invoice.requisites.length > 0 && (
                  <div className="space-y-1 rounded-xl border border-[rgb(var(--lp-edge)/var(--lp-edge-a))] p-4">
                    {invoice.requisites.map((r, i) => (
                      <div key={i} className="flex justify-between gap-3 text-sm py-1">
                        <span className="text-muted-foreground">{r.label}</span>
                        <span className="text-right font-medium break-all">{r.value}</span>
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={() => dragon ? setConfirmClose(true) : handleCancel()} disabled={closing} className="w-full py-3 rounded-xl border border-[rgb(var(--lp-edge)/var(--lp-edge-a))] text-muted-foreground font-medium disabled:opacity-50" data-testid="rubtopup-cancel">
                  {t(dragon ? "rubtopup.close_invoice" : "rubtopup.cancel")}
                </button>
                {dragon && <p className="text-xs text-muted-foreground text-center">{t("rubtopup.close_hint")}</p>}
              </>
            )}
          </div>
        </div>
        <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("rubtopup.close_title")}</AlertDialogTitle>
              <AlertDialogDescription>{t("rubtopup.close_confirm_hint")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={closing}>{t("rubtopup.keep_invoice")}</AlertDialogCancel>
              <button type="button" onClick={handleCancel} disabled={closing} className="px-4 py-2 rounded-xl bg-primary text-primary-foreground disabled:opacity-50" data-testid="rubtopup-confirm-close">{t(closing ? "rubtopup.closing" : "rubtopup.close_invoice")}</button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (restoring || restoreError || uncertainAttempt) {
    return <div className="h-[100dvh] bg-background flex flex-col" data-testid="rubtopup-restore-page">
      <InlinePageHeader onBack={handleBack} showBack={showInlineBack} />
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6 text-center">
        {restoring ? <Loader2 className="w-8 h-8 animate-spin" /> : <>
          <p>{t(uncertainAttempt ? "rubtopup.creation_retry_hint" : "rubtopup.restore_error")}</p>
          <button onClick={uncertainAttempt ? createInvoice : restoreInvoice} disabled={creating} className="w-full py-3 rounded-xl bg-primary text-primary-foreground">{t("common.try_again")}</button>
        </>}
      </div>
    </div>;
  }

  // ---------------------------------------------- Рельс не настроен / недоступен
  if (config && config.enabled === false) {
    return (
      <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col safe-top" data-testid="rubtopup-unavailable-page">
        <InlinePageHeader title={t("rubtopup.title")} onBack={handleBack} showBack={showInlineBack} />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center">
          <XCircle className="w-14 h-14 text-gray-400" />
          <p className="text-lg font-semibold text-foreground">{t("rubtopup.unavailable")}</p>
          <p className="text-sm text-gray-500 max-w-xs">{t("rubtopup.unavailable_desc")}</p>
          <button onClick={() => navigate("/topup")} className="mt-4 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold">
            {t("rubtopup.done")}
          </button>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ Шаг: ПДн
  if (step === "customer") {
    return (
      <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col" data-testid="rubtopup-customer-page">
        <InlinePageHeader title={t("rubtopup.payer_details")} onBack={handleBack} showBack={showInlineBack} />
        <div className="flex-1 overflow-y-auto overscroll-contain pb-[calc(5rem+var(--lp-bottom))]" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="p-6 space-y-4">
            {quote && <p className="text-sm text-center" data-testid="rubtopup-customer-summary">{t("rubtopup.confirm_summary", { rub: fmtRub(quote.rub_amount), amount: fmtCrypto(quote.receive_amount, currency), currency })}</p>}
            <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-700/40 px-3 py-2.5">
              <p className="text-xs leading-snug text-amber-900 dark:text-amber-100">{t(config?.provider === "dragontransfer" ? "rubtopup.kyc_payer_hint" : "rubtopup.payer_hint")}</p>
            </div>
            <Field label={t("rubtopup.first_name")} value={pii.first_name} onChange={(v) => setPii((p) => ({ ...p, first_name: v }))} placeholder="Иван" error={piiErrors.first_name} testid="pii-first-name" />
            <Field label={t("rubtopup.second_name")} value={pii.second_name} onChange={(v) => setPii((p) => ({ ...p, second_name: v }))} placeholder="Иванов" error={piiErrors.second_name} testid="pii-second-name" />
            <Field label={t("rubtopup.middle_name")} value={pii.middle_name} onChange={(v) => setPii((p) => ({ ...p, middle_name: v }))} placeholder="Иванович" error={piiErrors.middle_name} testid="pii-middle-name" />
            <Field label={t("rubtopup.phone")} value={pii.phone} onChange={(v) => setPii((p) => ({ ...p, phone: v }))} placeholder="+79990000000" error={piiErrors.phone} testid="pii-phone" type="tel" />
            <Field label={t("rubtopup.email")} value={pii.email} onChange={(v) => setPii((p) => ({ ...p, email: v }))} placeholder="mail@example.com" error={piiErrors.email} testid="pii-email" type="email" />

            {quoteError && <div role="alert" className="text-sm text-red-500">{t("rubtopup.quote_error")}<button type="button" onClick={() => setQuoteRevision(v => v + 1)} className="block underline">{t("common.try_again")}</button></div>}
            <button
              onClick={createInvoice}
              disabled={!piiComplete || creating || restoring || restoreError || !quote || !!rangeError}
              className={cn("w-full py-3.5 rounded-xl font-semibold transition-colors", piiComplete && !creating ? "bg-primary text-primary-foreground" : "bg-gray-200 dark:bg-gray-800 text-gray-400")}
              data-testid="rubtopup-pay"
            >
              {creating ? t("rubtopup.creating") : t(config?.provider === "dragontransfer" ? "rubtopup.continue_to_provider" : "rubtopup.pay")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ Шаг: сумма
  const limitHint = !(minRub && maxRub)
    ? ""
    : mode === "crypto" && minUnit && maxUnit
    ? t("rubtopup.limits_crypto", { min: minDisp, max: maxDisp, currency })
    : t("rubtopup.limits", { min: fmtInt(minRub), max: fmtInt(maxRub) });

  const canProceed = config?.enabled && !restoring && !restoreError && Number(amount) > 0 && !!quote && !quoting && !creating && !rangeError;

  return (
    <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col" data-testid="rubtopup-form-page">
      <InlinePageHeader title={t("rubtopup.title")} subtitle={singleCurrency ? soleCurrency : undefined} onBack={handleBack} showBack={showInlineBack} />
      <div className="flex-1 overflow-y-auto overscroll-contain pb-[calc(5rem+var(--lp-bottom))]" style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="p-6 space-y-6">
          {/* Валюта */}
          {singleCurrency ? (
            <div className="flex items-center gap-3 rounded-2xl bg-gray-50 dark:bg-gray-800/60 p-4" data-testid="rubtopup-currency-card">
              <img
                src={getCryptoIconUrl(soleCurrency)}
                alt={soleCurrency}
                className="w-11 h-11 rounded-full shrink-0"
                loading="lazy"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = getCryptoIconFallback(soleCurrency); }}
              />
              <div className="min-w-0">
                <p className="font-semibold text-foreground">{t("rubtopup.topup_currency_title", { currency: soleCurrency })}</p>
                <p className="text-sm text-muted-foreground leading-snug">{t("rubtopup.topup_currency_desc", { currency: soleCurrency })}</p>
              </div>
            </div>
          ) : (
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">{t("rubtopup.currency")}</label>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {cryptoOptions.map((c) => (
                  <button
                    key={c.currency}
                    type="button"
                    onClick={() => { haptic?.("light"); setCurrency(c.currency); }}
                    className={cn("flex items-center gap-2 px-3 py-2 rounded-xl border shrink-0 transition-colors", currency === c.currency ? "border-primary bg-primary/5" : "border-[rgb(var(--lp-edge)/var(--lp-edge-a))]")}
                    data-testid={`rubtopup-cur-${c.currency.toLowerCase()}`}
                  >
                    <img src={getCryptoIconUrl(c.currency)} alt={c.currency} className="w-6 h-6 rounded-full" loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = getCryptoIconFallback(c.currency); }} />
                    <span className="text-sm font-medium">{c.currency}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Переключатель режима */}
          <div className="flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1" data-testid="rubtopup-mode">
            <button type="button" onClick={() => { haptic?.("light"); setMode("rub"); setAmount(""); }} className={cn("flex-1 py-2 rounded-lg text-sm font-medium transition-colors", mode === "rub" ? "bg-white dark:bg-gray-700 shadow-sm text-foreground" : "text-muted-foreground")}>
              {t("rubtopup.mode_rub")}
            </button>
            <button type="button" onClick={() => { haptic?.("light"); setMode("crypto"); setAmount(""); }} className={cn("flex-1 py-2 rounded-lg text-sm font-medium transition-colors", mode === "crypto" ? "bg-white dark:bg-gray-700 shadow-sm text-foreground" : "text-muted-foreground")}>
              {t("rubtopup.mode_crypto", { currency })}
            </button>
          </div>

          {/* Сумма */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              {mode === "rub" ? t("rubtopup.amount_rub") : t("rubtopup.amount_crypto", { currency })}
            </label>
            <div className="relative">
              <input
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => { setInstantQuote(false); setAmount(e.target.value); }}
                placeholder="0"
                className={cn(
                  "w-full bg-gray-50 dark:bg-gray-800 rounded-xl p-4 pr-16 text-lg font-semibold text-foreground outline-none border",
                  rangeError ? "border-red-400" : "border-transparent"
                )}
                data-testid="rubtopup-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">{mode === "rub" ? "₽" : currency}</span>
            </div>

            {presets.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2" data-testid="rubtopup-presets">
                {presets.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => { haptic?.("light"); setInstantQuote(true); setAmount(String(p.value)); }}
                    className={cn(
                      "px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors",
                      Number(amount) === p.value ? "border-primary bg-primary/5 text-foreground" : "border-[rgb(var(--lp-edge)/var(--lp-edge-a))] text-muted-foreground"
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}

            <div className="min-h-[1.25rem] mt-2 text-sm flex items-center gap-2">
              {quoting ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              ) : rangeError ? (
                <span className="text-red-500" data-testid="rubtopup-range-error">
                  {t(`rubtopup.${rangeError.key}`, { amount: rangeError.amount, unit: rangeError.unit })}
                </span>
              ) : null}
            </div>
            {limitHint && (
              <p className="text-xs text-gray-400 mt-1" data-testid="rubtopup-limits">{limitHint}</p>
            )}
            {!quoting && !rangeError && Number(amount) > 0 && !quote && !quoteError && <p role="alert" className="text-sm text-red-500">{t("rubtopup.amount_precision_hint", { digits: mode === "rub" ? 2 : 6 })}</p>}
            {quoteError && !rangeError && <div role="alert" className="text-sm text-red-500">{t("rubtopup.quote_error")}<button type="button" onClick={() => setQuoteRevision(v => v + 1)} className="block underline" data-testid="rubtopup-retry-quote">{t("common.try_again")}</button></div>}
          </div>

          {/* Сводка по заявке */}
          {quote && !rangeError && (
            <div className="rounded-2xl bg-gray-50 dark:bg-gray-800/60 divide-y divide-gray-200/70 dark:divide-gray-700/50" data-testid="rubtopup-summary">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("rubtopup.you_receive_label")}</span>
                <span className="text-sm font-semibold text-green-500" data-testid="rubtopup-result">
                  {fmtCrypto(quote.receive_amount, currency)} {currency}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("rubtopup.rate_label")}</span>
                <span className="text-sm font-medium text-foreground">
                  {t("rubtopup.rate_value", {
                    rate: fmtRub(quote.provider_rate_rub_per_unit ?? quote.rate_rub_per_unit),
                    currency,
                  })}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <button
                  type="button"
                  onClick={() => { haptic?.("light"); toast(t("rubtopup.fee_included_hint")); }}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground"
                >
                  {t("rubtopup.fee_label")}
                  <HelpCircle className="w-3.5 h-3.5" />
                </button>
                <span className="text-sm font-medium text-foreground">{t("rubtopup.fee", { pct: quote.fee_pct })}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("rubtopup.total_label")}</span>
                <span className="text-base font-semibold text-foreground" data-testid="rubtopup-total">
                  {fmtRub(quote.rub_amount)} ₽
                </span>
              </div>
            </div>
          )}

          <button
            onClick={onPrimary}
            disabled={!canProceed}
            className={cn("w-full py-3.5 rounded-xl font-semibold transition-colors", canProceed ? "bg-primary text-primary-foreground" : "bg-gray-200 dark:bg-gray-800 text-gray-400")}
            data-testid="rubtopup-submit"
          >
            {creating ? t("rubtopup.creating") : requiresCustomer ? t("rubtopup.continue") : t("rubtopup.create")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RubTopUpPage;
